import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Bar, Legend } from "recharts";
import { BarChart3 } from "lucide-react";

export function SalesChart({ data, darkMode }) {
  const [range, setRange] = useState("30d");
  const filtered = range === "7d" ? data.slice(-7) : data;
  const maxRevenue = Math.max(...filtered.map(d => d.revenue), 1);

  return (
    <Card className={`border-0 shadow-soft rounded-2xl ${darkMode ? "bg-gray-800/80 border border-gray-700/50" : ""}`}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className={`text-base flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}>
          <BarChart3 className="w-4 h-4 text-emerald-500" /> Sales Overview
        </CardTitle>
        <div className={`flex items-center rounded-lg p-0.5 ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>
          {["7d", "30d"].map(r => (
            <button key={r} onClick={() => setRange(r)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${range === r ? (darkMode ? "bg-gray-600 shadow-sm text-white" : "bg-white shadow-sm text-navy-900") : (darkMode ? "text-gray-400" : "text-gray-500")}`}>
              {r === "7d" ? "7 Days" : "30 Days"}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={filtered} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#e0482d" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#e0482d" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#f0f0f0"} vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: darkMode ? "#9ca3af" : "#9ca3af" }} tickFormatter={d => { const dt = new Date(d); return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }} />
            <YAxis yAxisId="revenue" tick={{ fontSize: 11, fill: darkMode ? "#9ca3af" : "#9ca3af" }} tickFormatter={v => `₵${v}`} domain={[0, Math.ceil(maxRevenue * 1.1)]} />
            <YAxis yAxisId="orders" orientation="right" tick={{ fontSize: 11, fill: darkMode ? "#9ca3af" : "#9ca3af" }} domain={[0, "auto"]} allowDecimals={false} />
            <Tooltip
              contentStyle={{ borderRadius: "12px", border: darkMode ? "1px solid #4b5563" : "none", boxShadow: "0 4px 20px rgba(0,0,0,0.15)", fontSize: "13px", backgroundColor: darkMode ? "#1f2937" : "#fff", color: darkMode ? "#f3f4f6" : "#111827" }}
              itemStyle={{ color: darkMode ? "#e5e7eb" : "#111827" }}
              labelStyle={{ color: darkMode ? "#f9fafb" : "#111827", fontWeight: 600 }}
              formatter={(value, name) => [name === "revenue" ? `GHS ${value.toFixed(2)}` : value, name === "revenue" ? "Revenue" : "Orders"]}
              labelFormatter={d => new Date(d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
            />
            <Bar yAxisId="orders" dataKey="orders" name="Orders" fill={darkMode ? "#60a5fa" : "#0f172a"} opacity={darkMode ? 0.4 : 0.15} radius={[4, 4, 0, 0]} barSize={range === "7d" ? 32 : 10} />
            <Area yAxisId="revenue" type="monotone" dataKey="revenue" name="Revenue" stroke="#e0482d" strokeWidth={2} fill="url(#revenueGradient)" />
            <Legend
              verticalAlign="bottom"
              height={30}
              iconType="circle"
              iconSize={8}
              formatter={(value) => <span style={{ color: darkMode ? "#d1d5db" : "#6b7280", fontSize: "12px" }}>{value}</span>}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
